import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component, createRef } from "preact";
/** @import { RefObject } from "preact"; */

import { tfaCreateResponse } from "~/backend/validate.js";
import { ApiError, authedApiRequest, response } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import { PromiseSignal } from "~/util/promise.js";
import { literal, object, optional, parse, string } from "~/util/validate.js";
import Button from "~/component/Button.js";
import Field from "~/component/Field.js";
import Modal from "~/component/Modal.js";
import PasswordRules, { RULES } from "~/component/PasswordRules.js";
import Section from "~/component/Section.js";
import StepUp from "~/component/StepUp.js";
/** @import { TriggerProps } from "~/component/Tooltip.js"; */
import Tooltip from "~/component/Tooltip.js";
import { EnrollTotpModal, DisableTotpModal } from "~/component/Totp.js";
import Passkeys from "./Passkeys.js";
import input from "~/style/input.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./AccountSettings.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./AccountSettings.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {boolean} [flat]
 */
export default class AccountSettings extends Component {
  render() {
    const {
      flat
    } = this.props;
    const user = identity.me;
    const enterprise = identity.enterprise;
    return [__h(Section, {
      title: "Profile",
      subtitle: __h(ProfileId, {
        id: user?.id ?? ""
      }),
      icon: "person",
      flat: flat
    }, __h(Profile, {
      name: user?.name,
      onUpdateName: () => identity.fetch(),
      scim: enterprise?.is_scim_enabled
    })), __h(Section, {
      title: "Email",
      icon: "email",
      flat: flat
    }, __h(Email, {
      email: user?.email
    })), __h(Section, {
      title: "Password",
      icon: "password",
      flat: flat
    }, __h(Password, {})), __h(Section, {
      title: "Two-factor authentication",
      icon: "key",
      flat: flat
    }, __h(Totp, {
      enabled: Boolean(identity.me?.has_tfa)
    })), __h(Section, {
      title: "Passkeys",
      icon: "passkey",
      flat: flat
    }, __h(Passkeys, {}))];
  }
}

/**
 * @typedef {object} ProfileProps
 * @property {string | undefined} [name]
 * @property {(name: string) => void} onUpdateName
 * @property {boolean} [scim]
 */

/** @extends {Component<ProfileProps>} */
class Profile extends Component {
  #changeName = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */async ev => {
    ev.preventDefault();
    const el = ev.currentTarget;
    const form = new FormData(el);
    const data = parse(object({
      name: string
    }), Object.fromEntries(form.entries()));
    await authedApiRequest("PATCH", "/v2/me", {
      json: {
        name: data.name
      }
    });
    this.props.onUpdateName(data.name);
    alert("Profile updated!", {
      type: "success"
    });
  });
  render() {
    if (this.props.scim) {
      return __h("div", {
        "class": css.form
      }, __h(Field, {
        "for": "profile_name",
        label: "Full name"
      }, __h("input", {
        id: "profile_name",
        "class": input.input,
        value: this.props.name,
        disabled: true
      })), __h("p", {
        "class": css.blurb
      }, "Your company manages this account. To change your profile name, contact your company\n\t\t\t\t\t\tadministrator."));
    }
    const error = this.#changeName.error && ProfileError.from(this.#changeName.error);
    return __h("form", {
      "class": css.form,
      onSubmit: this.#changeName.invoke
    }, __h(Field, {
      "for": "profile_name",
      label: "Full name",
      error: error?.fields.name?.message,
      validity: {
        tooShort: "Names must be at least two characters long.",
        patternMismatch: "Names may only contain letters, spaces, hyphens or apostrophes."
      }
    }, __h("input", {
      "class": input.input,
      id: "profile_name",
      name: "name",
      required: true,
      minlength: 2,
      maxlength: 100,
      pattern: "[\xC0-\u017EA-Za-z0-9 _'\\\\-]+",
      defaultValue: this.props.name
    })), __h("p", {
      "class": css.error
    }, error?.message), __h("div", {
      "class": css.actions
    }, __h(Button, {
      type: "submit",
      loading: this.#changeName.loading
    }, " Update profile ")));
  }
}

/** @param {{ id: string }} props */
function ProfileId({
  id
}) {
  return __h(Tooltip, {
    placement: "right",
    contents: "Copy ID"
  }, (/** @type {TriggerProps} */trigger) => __h("button", {
    ...trigger,
    onClick: () => {
      navigator.clipboard.writeText(id);
      alert(`User ID copied to clipboard!`);
    },
    "class": css.copyId
  }, id));
}

/** @extends {ApiError<"name">} */
class ProfileError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {},
      name: {
        "validation_failed.name": "Name must be 2–100 characters and only contain letters, numbers and spaces.",
        "validation_failed.email": "Email address is invalid."
      }
    };
  }
}
class EmailError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        conflict: "There's already an account with this email.",
        invalid_credentials: "Your password is incorrect.",
        invalid_token: "Your verification has expired. Please try again.",
        tfa_required: "A two-factor authentication code is required.",
        email_locked: "This email address is temporarily unavailable. Please wait a bit and try again."
      }
    };
  }
}

/**
 * @typedef {object} EmailProps
 * @property {string | undefined} [email]
 */

/** @extends {Component<EmailProps>} */
class Email extends Component {
  email = signal(this.props.email);
  editing = signal(false);
  #stepUpRef = (/** @type {{ current: StepUp }} */createRef());
  changeEmail = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */async ev => {
    ev.preventDefault();
    const el = ev.currentTarget;
    const form = new FormData(el);
    const data = parse(object({
      email: string
    }), Object.fromEntries(form.entries()));
    if (data.email === this.props.email) {
      throw new Error(`${data.email} is already your account's email address.`);
    }
    await authedApiRequest("PATCH", "/v2/email", {
      json: {
        new_email: data.email,
        token: await this.#stepUpRef.current.requestToken("email_update")
      }
    });
    this.editing.value = false;
    alert(`Check ${data.email} to confirm your email address.`, {
      type: "success"
    });
  });
  render() {
    const error = this.changeEmail.error && EmailError.from(this.changeEmail.error);
    return [__h("div", {
      "class": css.form + " " + css.adjacent
    }, __h("span", {}, this.props.email), __h(Button, {
      onClick: () => {
        this.email.value = this.props.email ?? "";
        this.editing.value = true;
      }
    }, "Change email")), __h(Modal, {
      title: "Change email",
      open: this.editing.value,
      onClose: () => {
        this.editing.value = false;
        this.email.value = this.props.email;
      }
    }, __h("p", {}, "Enter your new email and your current password to change your account's email address."), __h("form", {
      "class": css.form,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        this.changeEmail.invoke(ev);
      }
    }, __h(Field, {
      "for": "email",
      label: "Email",
      error: error?.fields.email?.message
    }, __h("input", {
      "class": input.input,
      id: "email",
      type: "email",
      name: "email",
      value: this.email,
      onInput: async (/** @type {{ currentTarget: HTMLInputElement }} */e) => {
        this.email.value = e.currentTarget.value;
      },
      required: true
    })), __h(StepUp, {
      ref: this.#stepUpRef,
      error: error
    }), __h("p", {
      "class": css.error
    }, error?.message), __h("div", {
      "class": css.actions
    }, __h(Button, {
      level: "primary",
      type: "submit",
      loading: this.changeEmail.loading
    }, "Change email"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      disabled: this.changeEmail.loading
    }, "Cancel"))))];
  }
}

/**
 * @extends {Component<TotpProps>}
 *
 * @typedef {object} TotpProps
 * @property {boolean} enabled
 */
class Totp extends Component {
  /** @type {RefObject<Modal>} */
  #disableRef = createRef();
  #beginSetup = new PromiseSignal(async () => {
    const {
      data
    } = await authedApiRequest("POST", "/v2/tfa", {
      schema: response(tfaCreateResponse)
    });
    return data.tfa_auth_uri;
  });
  render() {
    const {
      enabled
    } = this.props;
    const error = this.#beginSetup.error && ApiError.from(this.#beginSetup.error);
    return [__h("div", {
      "class": css.form
    }, __h("div", {
      "class": css.adjacent
    }, enabled ? [__h("span", {}, "Two-factor authentication (2FA) is enabled on your account."), __h(Button, {
      kind: "negative",
      onClick: () => this.#disableRef.current?.open()
    }, "Disable 2FA")] : [__h("span", {}, "Two-factor authentication (2FA) adds an extra layer of security to your account in\n\t\t\t\t\t\t\t\t\taddition to your password."), __h(Button, {
      onClick: this.#beginSetup.invoke,
      loading: this.#beginSetup.loading
    }, "Enable 2FA")]), error && __h("p", {
      "class": css.error
    }, error.message)), __h(EnrollTotpModal, {
      uri: this.#beginSetup.data,
      onClose: () => {
        identity.fetch();
        this.#beginSetup.reset();
      }
    }), __h(DisableTotpModal, {
      modalRef: this.#disableRef
    })];
  }
}
class PasswordError extends ApiError {
  /** @override */
  get messages() {
    return {
      [ApiError.noField]: {
        invalid_credentials: "Your password is incorrect.",
        invalid_token: "Your verification has expired. Please try again.",
        tfa_required: "A two-factor authentication code is required."
      }
    };
  }
}
class Password extends Component {
  editing = signal(false);
  password = signal("");
  #stepUpRef = (/** @type {{ current: StepUp }} */createRef());
  changePassword = new PromiseSignal(/** @param {SubmitEvent & { currentTarget: HTMLFormElement }} ev */async ev => {
    const el = ev.currentTarget;
    ev.preventDefault();
    const form = new FormData(el);
    const data = parse(object({
      new: string,
      logout: optional(literal("on"))
    }), Object.fromEntries(form.entries()));
    await authedApiRequest("PATCH", "/v2/password", {
      json: {
        token: await this.#stepUpRef.current.requestToken("password_update"),
        new_password: data.new,
        logout_sessions: data.logout !== undefined
      }
    });
    this.editing.value = false;
    alert("Password updated!", {
      type: "success"
    });
  });
  render() {
    const error = this.changePassword.error && PasswordError.from(this.changePassword.error);
    return [__h("div", {
      "class": css.form + " " + css.adjacent
    }, __h("span", {
      "aria-hidden": "true"
    }, "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"), __h(Button, {
      onClick: () => {
        this.editing.value = true;
      }
    }, "Change password")), __h(Modal, {
      title: "Change password",
      open: this.editing.value,
      onClose: () => {
        this.editing.value = false;
        this.password.value = "";
      }
    }, __h("p", {}, "Enter your current password and your new password to change your account's password."), __h("form", {
      "class": css.form,
      onSubmit: (/** @type {SubmitEvent & { currentTarget: HTMLFormElement }} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        ev.preventDefault();
        this.changePassword.invoke(ev);
      }
    }, __h(StepUp, {
      ref: this.#stepUpRef,
      error: error
    }), __h(Field, {
      "for": "newpassword",
      label: "New password",
      error: error?.fields.new_password?.message,
      validity: {
        /* this is handled by the password rules component */
        patternMismatch: false
      }
    }, __h(PasswordRules, {
      password: this.password.value,
      key: this.editing.value ? "open" : "closed"
    }, __h("input", {
      "class": input.input,
      id: "newpassword",
      type: "password",
      name: "new",
      required: true,
      ...RULES,
      onInput: async (/** @type {{ currentTarget: HTMLInputElement }} */e) => {
        this.password.value = e.currentTarget.value;
      }
    }))), __h("label", {
      "class": css.label
    }, __h("input", {
      type: "checkbox",
      name: "logout",
      defaultChecked: true
    }), __h("span", {}, "Log out all other sessions")), __h("p", {
      "class": css.error
    }, error?.message), __h("div", {
      "class": css.actions
    }, __h(Button, {
      level: "primary",
      type: "submit",
      loading: this.changePassword.loading
    }, "Change password"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      formnovalidate: true,
      disabled: this.changePassword.loading
    }, "Cancel"))))];
  }
}